import React, { useState, useRef, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Type, Image, Film, Music, Play, Pause, Sparkles } from 'lucide-react';

/**
 * ItemThumb — renders a thumbnail for a display item (text, image, video, audio).
 * Used in the simple (default) view of LitePropertiesPanel.
 */
export const ItemThumb = ({ item, large = false, highlighted = false, onClick }) => {
  const sizeClass = large ? 'w-20 h-20 sm:w-20 sm:h-20 md:w-24 md:h-24 lg:w-28 lg:h-28' : 'w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16';
  const borderClass = highlighted
    ? 'border-2 border-ag-green'
    : 'border border-ag-black-400 hover:border-ag-green/50';

  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef(null);
  const audioRef = useRef(null);

  useEffect(() => {
    return () => {
      if (videoRef.current) videoRef.current.pause();
      if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
      setIsPlaying(false);
    };
  }, [item?.value]);

  const handlePlayToggle = useCallback((e) => {
    e.stopPropagation();
    if (item?.category === 'video' && videoRef.current) {
      if (isPlaying) { videoRef.current.pause(); setIsPlaying(false); }
      else { videoRef.current.play(); setIsPlaying(true); }
    } else if (item?.category === 'audio' && item.value) {
      if (isPlaying) { audioRef.current?.pause(); setIsPlaying(false); }
      else {
        if (!audioRef.current) {
          audioRef.current = new Audio(item.value);
          audioRef.current.addEventListener('ended', () => setIsPlaying(false));
        }
        audioRef.current.play();
        setIsPlaying(true);
      }
    }
  }, [isPlaying, item]);

  // Auto-play video/gif on hover
  const handleMouseEnter = useCallback(() => {
    if (item?.category === 'video' && videoRef.current && !isPlaying) {
      videoRef.current.play().catch(() => {});
      setIsPlaying(true);
    }
  }, [item, isPlaying]);

  const handleMouseLeave = useCallback(() => {
    if (item?.category === 'video' && videoRef.current && isPlaying) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
      setIsPlaying(false);
    }
  }, [item, isPlaying]);

  const btnSize = large ? 10 : 8;
  const playBtnClass = large ? 'w-8 h-8' : 'w-5 h-5';

  if (!item) {
    return (
      <div className={`${sizeClass} border border-ag-black-400 rounded-lg overflow-hidden bg-ag-black-600 flex-shrink-0`}>
        <div className="w-full h-full bg-gradient-to-br from-ag-black-600 to-ag-black-800" />
      </div>
    );
  }

  const renderContent = () => {
    switch (item.category) {
      case 'image':
        return item.value ? (
          <img crossOrigin='anonymous' src={item.value} alt={item.layerName || item.elementName || 'Image'} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-gray-500">
            <Image size={large ? 24 : 16} />
            {large && <span className="text-[10px] mt-1">Image</span>}
          </div>
        );

      case 'video':
        return item.value ? (
          <div className="relative w-full h-full">
            <video crossOrigin='anonymous' ref={videoRef} src={item.value} poster={item.poster || undefined} className="w-full h-full object-cover" muted playsInline preload="auto" onEnded={() => setIsPlaying(false)} onLoadedData={(e) => { if (!item.poster) { e.target.currentTime = 0.1; } }} />
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-gray-500">
            <Film size={large ? 24 : 16} />
            {large && <span className="text-[10px] mt-1">Video</span>}
          </div>
        );

      case 'audio':
        return (
          <div className="relative flex flex-col items-center justify-center h-full bg-gradient-to-br from-purple-900/60 to-blue-900/60 group">
            <Music size={large ? 24 : 16} className={`text-purple-300 ${isPlaying ? 'animate-pulse' : ''}`} />
            <span className={`text-purple-200 leading-tight mt-1 truncate max-w-full px-1 ${large ? 'text-[10px]' : 'text-[8px]'}`}>
              {item.elementName || 'Audio'}
            </span>
            {item.value && (
              <div className="absolute inset-0 flex items-center justify-center">
                <button className={`${playBtnClass} rounded-full bg-black/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/80`} onClick={handlePlayToggle} title={isPlaying ? 'Pause' : 'Play preview'}>
                  {isPlaying ? <Pause size={btnSize} className="text-white" /> : <Play size={btnSize} className="text-white ml-0.5" />}
                </button>
              </div>
            )}
          </div>
        );

      case 'text':
        return (
          <div className="flex flex-col items-center justify-center h-full p-1.5 text-center overflow-hidden">
            <Type size={large ? 18 : 12} className="text-ag-green mb-0.5 flex-shrink-0" />
            <span className={`text-white leading-tight overflow-hidden text-ellipsis ${large ? 'text-[11px] line-clamp-3' : 'text-[9px] line-clamp-2'}`}>
              {item.value || 'Empty text'}
            </span>
          </div>
        );

      default:
        return <div className="w-full h-full bg-gradient-to-br from-ag-black-600 to-ag-black-800" />;
    }
  };

  return (
    <div
      className={`${sizeClass} ${borderClass} rounded-lg overflow-hidden bg-ag-black-600 flex-shrink-0 flex items-center justify-center transition-all cursor-pointer hover:scale-105 relative`}
      onClick={onClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      title={item.layerName || item.elementName || item.category}
    >
      {renderContent()}
      {item.isAiGenerated && (
        <div className="absolute bottom-0 left-0 right-0 bg-black/70 flex items-center justify-center gap-1 py-0.5">
          <Sparkles size={large ? 10 : 8} className="text-ag-green" />
          <span className={`text-ag-green font-sourceSans font-semibold ${large ? 'text-[10px]' : 'text-[8px]'}`}>AI</span>
        </div>
      )}
    </div>
  );
};

/**
 * KeyboardHint — shows navigation instructions at the bottom of the panel.
 * Accepts optional onPrev / onNext callbacks to make the chevron icons clickable.
 */
export const KeyboardHint = ({ showScroll = false, onPrev, onNext }) => (
  <div className="flex items-center justify-center gap-1 mt-1.5 text-ag-black-300 text-[11px]">
    <span>Press</span>
    <KeyHint onClick={onPrev}><ChevronLeft size={20} /></KeyHint>
    <KeyHint onClick={onNext}><ChevronRight size={20} /></KeyHint>
    <span>{showScroll ? 'or scroll to move easily to components' : 'on your keyboard to move easily to components'}</span>
  </div>
);

const KeyHint = ({ children, onClick }) => (
  <span
    className={`inline-flex items-center justify-center w-[18px] h-[18px] rounded border border-ag-black-400 bg-ag-black-600 text-ag-green ${onClick ? 'cursor-pointer hover:bg-ag-black-500 hover:border-ag-green transition-colors' : ''}`}
    onClick={onClick}
    role={onClick ? 'button' : undefined}
    tabIndex={onClick ? 0 : undefined}
    onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
  >
    {children}
  </span>
);
